import type { UIMessage } from "ai";

export interface DataSetContext {
    dataSetId?: string;
    dataSetName?: string;
    dataSetDescription?: string;
    schemaId?: string;
    schemaName?: string;
    businessAreaId?: string;
    version?: number;
    recordCount?: number;
    // Creator & update info
    createdByName?: string;
    createdByEmail?: string;
    createdDate?: string;
    updatedByName?: string;
    updatedByEmail?: string;
    updatedDate?: string;
    // Quality metrics
    erroredRecordsCount?: number;
    runningJobsCount?: number;
    // Rating & tags
    rating?: number;
    tags?: {
        intent?: string[];
        trustworthiness?: string[];
        quality?: string[];
        state?: string[];
        sourceType?: string[];
    };
}

export interface DataObjectFieldError {
    field: string;
    reasons: string[];
}

export interface DataObjectMetadata {
    addressPrecision?: {
        qualityScore: string;
        label: string;
        isPartialMatch?: boolean;
    };
    emailVerification?: Array<{
        field: string;
        status: string;
        label: string;
        domain?: string | null;
    }>;
    consent?: {
        state: "granted" | "revoked" | "not_recorded";
        selfServiceVerified?: boolean;
        verificationLink?: "pending" | "expired";
    };
}

export interface DataObjectContextItem {
    dataObjectId: string;
    values?: Record<string, unknown>;
    errors?: DataObjectFieldError[];
    metadata?: DataObjectMetadata;
}

export interface ValidationRule {
    type?: string;
    pattern?: string;
    message?: string;
}

export interface SchemaFieldSummary {
    fieldId: string;
    name: string;
    dataType: string;
    description?: string;
    isPrimaryKey: boolean;
    isRequired: boolean;
    // Validation constraints
    min?: number;
    max?: number;
    validationRules?: ValidationRule[];
    // Golden-thread / picklist metadata (item 2): links a coded field to its
    // picklist config + Business Area Data (data domain) governance record.
    picklistType?: "static" | "dynamic";
    dataDomainId?: string;
    staticPicklist?: Array<{ code: string; description: string }>;
    classifiers?: Array<{ tagId?: string; tagName?: string }>;
}

export interface ProfilingStatItem {
    fieldName: string;
    totalRecords?: number;
    uniquenessPct?: number;
    completePct?: number;
    successDataCount?: number;
    erroredDataCount?: number;
    nullPct?: number;
    knownFormats?: {
        date?: number;
        phone?: number;
        uuid?: number;
        email?: number;
        url?: number;
    };
}

export interface CommentItem {
    commentId: string;
    content: string;
    createdByName?: string;
    createdByEmail?: string;
    createdDate?: string;
    pinned?: boolean;
}

export interface DataSetMode {
    action?: "view" | "edit" | "create";
    permission?: { canAdd?: boolean; canEdit?: boolean };
    schemaFields?: SchemaFieldSummary[];
}

export function extractDataSetContext(dataSet: any): DataSetContext | undefined {
    if (!dataSet) return undefined;

    const context: DataSetContext = {
        dataSetId: dataSet?.dataSetId,
        dataSetName: dataSet?.dataSetName,
        dataSetDescription: dataSet?.dataSetDescription,
        schemaId: dataSet?.schemaId,
        schemaName: dataSet?.schemaName,
        businessAreaId: dataSet?.businessAreaId,
        version: dataSet?.version,
    };

    // Add optional fields only if they exist
    if (dataSet?.recordCount !== undefined) context.recordCount = dataSet.recordCount;

    // Creator & update info
    if (dataSet?.createdByName) context.createdByName = dataSet.createdByName;
    if (dataSet?.createdByEmail) context.createdByEmail = dataSet.createdByEmail;
    if (dataSet?.createdDate) context.createdDate = dataSet.createdDate;
    if (dataSet?.updatedByName) context.updatedByName = dataSet.updatedByName;
    if (dataSet?.updatedByEmail) context.updatedByEmail = dataSet.updatedByEmail;
    if (dataSet?.updatedDate) context.updatedDate = dataSet.updatedDate;

    // Quality metrics
    if (dataSet?.erroredRecordsCount !== undefined) context.erroredRecordsCount = dataSet.erroredRecordsCount;
    if (dataSet?.runningJobsCount !== undefined) context.runningJobsCount = dataSet.runningJobsCount;

    // Rating & tags from metadata
    if (dataSet?.metadata?.rating !== undefined) context.rating = dataSet.metadata.rating;
    if (dataSet?.metadata?.tags && typeof dataSet.metadata.tags === "object") {
        const t = dataSet.metadata.tags;
        const tags: DataSetContext["tags"] = {};
        if (Array.isArray(t.intent) && t.intent.length > 0) tags.intent = t.intent;
        if (Array.isArray(t.trustworthiness) && t.trustworthiness.length > 0) tags.trustworthiness = t.trustworthiness;
        if (Array.isArray(t.quality) && t.quality.length > 0) tags.quality = t.quality;
        if (Array.isArray(t.state) && t.state.length > 0) tags.state = t.state;
        if (Array.isArray(t.sourceType) && t.sourceType.length > 0) tags.sourceType = t.sourceType;
        if (Object.keys(tags).length > 0) context.tags = tags;
    }

    return context;
}

const ADDRESS_QUALITY_LABELS: Record<string, string> = {
    ROOFTOP: "Exact address match",
    EXACT_COORDINATES: "Manual coordinates (user-specified pin)",
    RANGE_INTERPOLATED: "Street range (approximate) — less precise than exact address",
    GEOMETRIC_CENTER: "Area centroid (area-level match, lower precision)",
    APPROXIMATE: "Broad region match (low precision)",
};

const EMAIL_STATUS_LABELS: Record<string, string> = {
    verification_pending: "Email verification in progress",
    domain_reachable: "Email domain is reachable (can receive mail)",
    mx_ok_smtp_reachable: "Email domain verified",
    mx_ok_smtp_unreachable: "Email domain found but mail server unreachable",
    mx_ok_smtp_skipped: "Email domain verified (mail server present)",
    no_mx: "No mail server found for this domain",
    dns_error: "DNS lookup failed for email domain",
    invalid_format: "Invalid email format",
    disposable_domain: "Disposable / throwaway email domain",
    verification_error: "Email verification could not be completed",
    consent_email_delivered: "Consent email delivered successfully",
    consent_email_bounced: "Consent email bounced (permanent failure)",
    consent_email_temporary_bounce: "Consent email temporarily bounced",
    consent_email_complaint: "Consent email marked as spam by recipient",
};

export function buildDataObjectMetadata(d: any, schemaFields: any[] | undefined): DataObjectMetadata | undefined {
    if (!d) return undefined;
    const metadata: DataObjectMetadata = {};

    const geo = d._system_geodata;
    if (geo && typeof geo.qualityScore === "string" && geo.qualityScore) {
        metadata.addressPrecision = {
            qualityScore: geo.qualityScore,
            label: ADDRESS_QUALITY_LABELS[geo.qualityScore] || geo.qualityScore,
            ...(geo.isPartialMatch ? { isPartialMatch: true } : {}),
        };
    }

    const vr = d.verificationResults || d._verificationResults;
    if (vr && typeof vr === "object") {
        const emailFieldIds = new Set(
            (Array.isArray(schemaFields) ? schemaFields : []).filter((f: any) => f?.dataType === "email" && f?.fieldId).map((f: any) => f.fieldId),
        );
        const emailVerification: NonNullable<DataObjectMetadata["emailVerification"]> = [];
        for (const [fieldId, result] of Object.entries<any>(vr)) {
            if (!result || typeof result.status !== "string") continue;
            if (emailFieldIds.size > 0 && !emailFieldIds.has(fieldId)) continue;
            const fieldName = (Array.isArray(schemaFields) ? schemaFields : []).find((f: any) => f?.fieldId === fieldId)?.name || fieldId;
            emailVerification.push({
                field: fieldName,
                status: result.status,
                label: EMAIL_STATUS_LABELS[result.status] || result.status,
                ...(result.domain !== undefined ? { domain: result.domain } : {}),
            });
        }
        if (emailVerification.length > 0) metadata.emailVerification = emailVerification;
    }

    if (d._consent !== undefined || d._verificationId !== undefined || d._verified !== undefined) {
        const consent: NonNullable<DataObjectMetadata["consent"]> = {
            state: d._consent === true ? "granted" : d._consent === false ? "revoked" : "not_recorded",
        };
        if (d._verified === true) consent.selfServiceVerified = true;
        if (d._verificationId !== undefined && d._verified !== true) {
            const expiry = d._verificatioExpiry ? Date.parse(d._verificatioExpiry) : NaN;
            consent.verificationLink = !isNaN(expiry) && expiry < Date.now() ? "expired" : "pending";
        }
        metadata.consent = consent;
    }

    return Object.keys(metadata).length > 0 ? metadata : undefined;
}

// Strip framework-only / heavy bookkeeping fields from a data object so the
// payload sent to the LLM stays focused on values the user can reason about.
const HIDDEN_FIELDS = new Set(["errors", "_verificationId", "_verificatioExpiry", "_verified", "_consent", "version", "_version", "sNo"]);

// Maximum number of data objects to send to the LLM to prevent token overflow
// and rate limiting. The LLM generates filtering criteria; the UI applies it
// to the full dataset.
const MAX_DATA_OBJECTS_FOR_LLM = 20;

// Builds the per-message data-object context for the LLM. Values are re-keyed
// from fieldId → field NAME so the LLM works in the same name-space it sees in
// the schemaFields block (and the one it proposes back via updateDataObject /
// findDataObjects). Framework-only bookkeeping fields are stripped.
//
// SAMPLING: To prevent token overflow, only the first MAX_DATA_OBJECTS_FOR_LLM
// records are sent. The LLM uses these samples to understand the data structure
// and generate filtering criteria, which the UI then applies to the full dataset.
export function trimDataObjectsForContext(dataObjects: any[] | undefined, schemaFields: any[] | undefined): DataObjectContextItem[] {
    if (!Array.isArray(dataObjects)) return [];

    const fieldIdToName = new Map<string, string>();
    if (Array.isArray(schemaFields)) {
        for (const f of schemaFields) {
            if (f?.fieldId && f?.name) fieldIdToName.set(f.fieldId, f.name);
        }
    }

    // Prioritise errored records in the sample so the assistant can answer
    // "show me errored records" / "bad quality records" even in large datasets
    // where the naive first-N slice might contain none. Errored records first,
    // then clean ones, capped at MAX_DATA_OBJECTS_FOR_LLM.
    const hasErrors = (d: any) => Array.isArray(d?.errors) && d.errors.length > 0;
    const errored = dataObjects.filter(hasErrors);
    const clean = dataObjects.filter((d) => !hasErrors(d));
    const sampledDataObjects = [...errored, ...clean].slice(0, MAX_DATA_OBJECTS_FOR_LLM);

    return sampledDataObjects.map((d: any) => {
        const values: Record<string, unknown> = {};
        Object.keys(d || {}).forEach((k) => {
            if (k === "dataObjectId") return;
            if (HIDDEN_FIELDS.has(k)) return;
            // Map known schema-field IDs to their human name; skip anything else
            // (dataSetId / schemaId / businessAreaId / unknown keys).
            const name = fieldIdToName.get(k);
            if (!name) return;
            values[name] = d[k];
        });
        const item: DataObjectContextItem = { dataObjectId: d?.dataObjectId, values };
        // Translate the record's error array (keyed by fieldId) into a
        // name-keyed summary the LLM can quote back with reasons.
        if (hasErrors(d)) {
            const errs: DataObjectFieldError[] = [];
            for (const e of d.errors) {
                const fieldName = fieldIdToName.get(e?.name) || e?.name || "record";
                const reasons = Array.isArray(e?.errors) ? e.errors.map((r: any) => String(r)) : e?.errors ? [String(e.errors)] : [];
                errs.push({ field: fieldName, reasons });
            }
            if (errs.length > 0) item.errors = errs;
        }
        // Attach the per-record verification / consent metadata block (address
        // precision, email deliverability, consent + self-service status).
        const metadata = buildDataObjectMetadata(d, schemaFields);
        if (metadata) item.metadata = metadata;
        return item;
    });
}

export function summariseSchemaFields(schemaFields: any[] | undefined): SchemaFieldSummary[] {
    if (!Array.isArray(schemaFields)) return [];
    return schemaFields.map((f: any) => {
        const field: SchemaFieldSummary = {
            fieldId: f?.fieldId,
            name: f?.name,
            dataType: f?.dataType,
            description: f?.description,
            isPrimaryKey: !!f?.isPrimaryKey,
            isRequired: !!f?.isRequired,
        };

        // Only include validation constraints if they exist
        if (f?.min !== undefined) field.min = f.min;
        if (f?.max !== undefined) field.max = f.max;
        if (f?.validationRules) field.validationRules = f.validationRules;

        // Golden-thread / picklist metadata (item 2). Forwarded so the LLM knows
        // which fields are coded picklists (and resolvePicklistContext can use them).
        if (f?.picklistType) field.picklistType = f.picklistType;
        if (f?.dataDomainId) field.dataDomainId = f.dataDomainId;
        if (Array.isArray(f?.staticPicklist) && f.staticPicklist.length > 0) {
            field.staticPicklist = f.staticPicklist.map((p: any) => ({ code: p?.code, description: p?.description }));
        }
        if (Array.isArray(f?.classifiers) && f.classifiers.length > 0) {
            field.classifiers = f.classifiers.map((c: any) => ({ tagId: c?.tagId, tagName: c?.tagName }));
        }

        return field;
    });
}

export function extractProfilingStats(fieldInsights: any[] | undefined): ProfilingStatItem[] {
    if (!Array.isArray(fieldInsights)) return [];
    return fieldInsights
        .filter((item: any) => item?.fieldName) // Only include items with fieldName
        .map((item: any) => {
            const stat: ProfilingStatItem = {
                fieldName: item.fieldName,
            };

            // Only include fields that have values
            if (item?.profilingStats?.totalRecords !== undefined) stat.totalRecords = item.profilingStats.totalRecords;
            if (item?.profilingStats?.uniquenessPct !== undefined) stat.uniquenessPct = item.profilingStats.uniquenessPct;
            if (item?.profilingStats?.completePct !== undefined) stat.completePct = item.profilingStats.completePct;
            if (item?.successDataCount !== undefined) stat.successDataCount = item.successDataCount;
            if (item?.erroredDataCount !== undefined) stat.erroredDataCount = item.erroredDataCount;
            if (item?.profilingStats?.nullPct !== undefined) stat.nullPct = item.profilingStats.nullPct;
            if (item?.profilingStats?.knownFormats) stat.knownFormats = item.profilingStats.knownFormats;

            return stat;
        });
}

export function extractComments(commentsData: any): CommentItem[] {
    if (!commentsData?.Items || !Array.isArray(commentsData.Items)) return [];
    return commentsData.Items.filter((item: any) => item?.commentId && item?.content) // Only include valid comments
        .map((item: any) => {
            const comment: CommentItem = {
                commentId: item.commentId,
                content: item.content,
            };

            // Only include optional fields if they exist
            if (item?.createdByName) comment.createdByName = item.createdByName;
            if (item?.createdByEmail) comment.createdByEmail = item.createdByEmail;
            if (item?.createdDate) comment.createdDate = item.createdDate;
            if (item?.pinned !== undefined) comment.pinned = item.pinned;

            return comment;
        });
}

export function getMessageText(message: UIMessage): string {
    if (!Array.isArray(message.parts)) return "";
    return message.parts
        .filter((p: any) => p?.type === "text")
        .map((p: any) => p.text || "")
        .join("");
}
